import { autocompletion } from "@codemirror/autocomplete"

const announcementConditionRules = {
  Vestigial: {
    type: "boolean",
    operators: ["=", "==", "!=", "!"],
    values: ["True", "False"],
    poe_version: 1
  }
}

const legacyBooleanConditionNames = [
  "Identified",
  "Corrupted",
  "ElderItem",
  "ShaperItem",
  "FracturedItem",
  "SynthesisedItem"
]

const legacyConditionRules = {
  Rarity: {
    type: "enum",
    operators: ["=", "==", "!=", "!", "<", ">", "<=", ">="],
    values: ["Normal", "Magic", "Rare", "Unique"]
  },
  HasInfluence: {
    type: "enum",
    operators: ["=", "==", "!=", "!"],
    values: ["Shaper", "Elder", "Crusader", "Hunter", "Redeemer", "Warlord", "None"]
  },
  ...Object.fromEntries(
    legacyBooleanConditionNames.map(condition => [
      condition,
      {
        type: "boolean",
        operators: ["=", "==", "!=", "!"],
        values: ["True", "False"]
      }
    ])
  )
}

function announcementRulesForRealm(realm) {
  const poeVersion = realm === "poe2" ? 2 : 1

  return Object.fromEntries(
    Object.entries(announcementConditionRules).filter(
      ([, rule]) => !rule.poe_version || rule.poe_version === poeVersion
    )
  )
}

function fallbackConditionRulesForRealm(realm) {
  return { ...legacyConditionRules, ...announcementRulesForRealm(realm) }
}

// Game data - loaded from our Elixir API
let gameData = {
  blockTypes: ['Show', 'Hide'],
  conditions: [],
  conditionRules: fallbackConditionRulesForRealm('pc'),
  actions: [],
  operators: ['=', '==', '!=', '!', '<', '>', '<=', '>='],
  rarities: [],
  influences: [],
  booleans: [],
  soundIds: [],
  effectColors: [],
  fontSizes: [],
  baseTypes: [],
  itemClasses: [],
  minimapIconSizes: [],
  minimapIconShapes: []
}
let latestGameDataRequest = 0

function applyAnnouncementRulesForRealm(conditionRules) {
  const announcementConditionNames = new Set(Object.keys(announcementConditionRules))
  const retainedConditions = gameData.conditions.filter(
    condition => !announcementConditionNames.has(condition)
  )
  const retainedConditionRules = Object.fromEntries(
    Object.entries(gameData.conditionRules).filter(
      ([condition]) => !announcementConditionNames.has(condition)
    )
  )

  gameData.conditions = [...retainedConditions, ...Object.keys(conditionRules)]
  gameData.conditionRules = { ...retainedConditionRules, ...conditionRules }
  window.gameData = gameData
}

// Load game data from API
async function loadGameData(realm = 'pc') {
  const requestId = ++latestGameDataRequest
  const fallbackAnnouncementRules = announcementRulesForRealm(realm)
  const fallbackConditionRules = fallbackConditionRulesForRealm(realm)
  applyAnnouncementRulesForRealm(fallbackAnnouncementRules)

  try {
    // Get CSRF token from meta tag
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
    
    const response = await fetch(`/api/game-data/${realm}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      },
      credentials: 'same-origin'
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data = await response.json()

    if (requestId !== latestGameDataRequest) {
      return
    }
    
    // Update gameData with real data from our Elixir modules
    gameData.conditions = data.conditions || []
    gameData.conditionRules = data.condition_rules || fallbackConditionRules
    gameData.actions = data.actions || []
    gameData.rarities = data.rarities || []
    gameData.influences = data.influences || []
    gameData.booleans = data.booleans_values || []
    gameData.soundIds = data.sound_ids || []
    gameData.effectColors = data.effect_colors || []
    gameData.fontSizes = data.font_sizes || []
    gameData.baseTypes = data.base_types || []
    gameData.itemClasses = data.item_classes || []
    gameData.minimapIconSizes = data.minimap_icon_sizes || []
    gameData.minimapIconShapes = data.minimap_icon_shapes || []
    
    // Make game data available globally for linting
    window.gameData = gameData

  } catch (error) {
    if (requestId !== latestGameDataRequest) {
      return
    }

    console.error('Failed to load game data:', error)
    // Fallback to minimal static data
    gameData.conditions = [...new Set([
      'BaseType',
      'Class',
      'Rarity',
      'ItemLevel',
      ...Object.keys(fallbackConditionRules)
    ])]
    gameData.conditionRules = fallbackConditionRules
    gameData.actions = ['SetTextColor', 'PlayAlertSound', 'PlayEffect']
    gameData.rarities = ['Normal', 'Magic', 'Rare', 'Unique']
    gameData.booleans = ['True', 'False']
    gameData.soundIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]
    gameData.effectColors = ["Red", "Green", "Blue", "White", "Yellow"]
    gameData.fontSizes = Array.from({length: 27}, (_, i) => i + 18)
    window.gameData = gameData
  }
}

// Initialize game data when module loads
loadGameData()

export function getConditionValueCompletions(
  lineText,
  currentWord,
  conditionRules = gameData.conditionRules
) {
  const trimmedLine = lineText.trimStart()
  const condition = trimmedLine.split(/\s+/, 1)[0]
  const rule = conditionRules[condition]

  if (!rule || !trimmedLine.startsWith(`${condition} `) || currentWord.length === 0) {
    return []
  }

  if (!["boolean", "enum", "mixed"].includes(rule.type) || !Array.isArray(rule.values)) {
    return []
  }

  const detail =
    rule.type === "boolean"
      ? "Boolean value"
      : rule.type === "enum"
        ? "Allowed value"
        : "Supported value"

  return rule.values
    .filter(value => value.toLowerCase().startsWith(currentWord.toLowerCase()))
    .map(value => ({ label: value, type: "constant", detail }))
}

// Smart autocomplete function
function getCompletions(context) {
  try {
    const line = context.state.doc.lineAt(context.pos)
    const lineText = line.text
    const cursorInLine = context.pos - line.from
    
    // Check if we're inside quotes first
    const isInsideQuotes = (() => {
      const beforeCursor = lineText.slice(0, cursorInLine)
      const quoteCount = (beforeCursor.match(/"/g) || []).length
      return quoteCount % 2 === 1
    })()
    
    // Word extraction - handle quotes differently
    let wordStart = cursorInLine
    let currentWord
    
    if (isInsideQuotes) {
      // Inside quotes: find the start of the quoted string
      while (wordStart > 0 && lineText[wordStart - 1] !== '"') {
        wordStart--
      }
      // Extract the word without the opening quote
      currentWord = lineText.slice(wordStart, cursorInLine)
    } else {
      // Outside quotes: normal word extraction
      while (wordStart > 0 && /[\w"']/.test(lineText[wordStart - 1])) {
        wordStart--
      }
      currentWord = lineText.slice(wordStart, cursorInLine).replace(/['"]/g, '')
    }
    
    const completions = []
    
    // Check if THIS SPECIFIC LINE already has an action or condition (not the whole text)
    const currentLineOnly = lineText.trim()
    const isCurrentLineAction = gameData.actions && gameData.actions.some(action => currentLineOnly.startsWith(action))
    const isCurrentLineCondition = gameData.conditions && gameData.conditions.some(condition => currentLineOnly.startsWith(condition))
    
    // 1. BASIC KEYWORDS - Block types, conditions, actions (only at start of line)
    const textBeforeCursor = lineText.slice(0, cursorInLine)
    const isAtLineStart = cursorInLine === 0 || /^\s*$/.test(textBeforeCursor) || textBeforeCursor.trim() === currentWord
    const firstWordInLine = lineText.trim().split(/\s+/)[0]
    

    
    // Only suggest keywords if we're at the start of the line AND this line doesn't already have an action/condition
    if (isAtLineStart && currentWord.length > 0 && !isCurrentLineAction && !isCurrentLineCondition) {
      // Block types
      gameData.blockTypes.forEach(item => {
        if (item.toLowerCase().startsWith(currentWord.toLowerCase())) {
          completions.push({ label: item, type: 'keyword', detail: 'Block type' })
        }
      })
      
      // Conditions  
      gameData.conditions.forEach(item => {
        if (item.toLowerCase().startsWith(currentWord.toLowerCase())) {
          completions.push({ label: item, type: 'property', detail: 'Condition' })
        }
      })
      
      // Actions
      gameData.actions.forEach(item => {
        if (item.toLowerCase().startsWith(currentWord.toLowerCase())) {
          completions.push({ label: item, type: 'function', detail: 'Action' })
        }
      })
    }
    
    // 2. ACTION VALUES (no operators needed) - after action names
    
    // PlayEffect colors (direct after PlayEffect)
    if (/PlayEffect\s+/.test(lineText) && currentWord.length > 0) {
      // Add "None" option to disable effects
      if ('none'.startsWith(currentWord.toLowerCase())) {
        completions.push({ label: 'None', type: 'keyword', detail: 'Disable effect' })
      }
      
      gameData.effectColors.forEach(color => {
        if (color.toLowerCase().startsWith(currentWord.toLowerCase())) {
          completions.push({ label: color, type: 'constant', detail: 'Effect color' })
        }
      })
    }
    
    // PlayEffect Temp parameter (after color)
    if (/PlayEffect\s+\w+\s+/.test(lineText) && currentWord.length > 0) {
      if ('temp'.startsWith(currentWord.toLowerCase())) {
        completions.push({ label: 'Temp', type: 'keyword', detail: 'Temporary effect' })
      }
    }
    
    // SetFontSize values (direct after SetFontSize)
    if (/SetFontSize\s+/.test(lineText) && currentWord.length > 0) {
      gameData.fontSizes.forEach(size => {
        if (size.toString().startsWith(currentWord)) {
          completions.push({ label: size.toString(), type: 'number', detail: 'Font size' })
        }
      })
    }
    
    // Color values for SetTextColor, SetBackgroundColor, SetBorderColor
    if (/Set(Text|Background|Border)Color\s+/.test(lineText)) {
      // Add color picker option at the top (when cursor is after the action name)
      if (currentWord === '') {
        completions.push({ 
          label: '🎨 Pick Color...', 
          type: 'function', 
          detail: 'Open color picker',
          apply: 'COLOR_PICKER_TRIGGER'
        })
      }
      
            // Only show other suggestions if user is typing something
      if (currentWord.length > 0) {
        
        // RGB values 0-255
        for (let i = 0; i <= 255; i += 25) {
          if (i.toString().startsWith(currentWord)) {
            completions.push({ label: i.toString(), type: 'number', detail: 'RGB value (0-255)' })
          }
        }
        
        // Common color presets
        const colorPresets = [
          { name: 'Red', rgb: '255 0 0' },
          { name: 'Green', rgb: '0 255 0' },
          { name: 'Blue', rgb: '0 0 255' },
          { name: 'White', rgb: '255 255 255' },
          { name: 'Black', rgb: '0 0 0' },
          { name: 'Yellow', rgb: '255 255 0' },
          { name: 'Cyan', rgb: '0 255 255' },
          { name: 'Magenta', rgb: '255 0 255' },
          { name: 'Orange', rgb: '255 165 0' },
          { name: 'Purple', rgb: '128 0 128' },
          { name: 'Pink', rgb: '255 192 203' },
          { name: 'Brown', rgb: '165 42 42' },
          { name: 'Grey', rgb: '128 128 128' }
        ]
        
        colorPresets.forEach(preset => {
          if (preset.name.toLowerCase().startsWith(currentWord.toLowerCase())) {
            completions.push({ 
              label: preset.name, 
              type: 'constant', 
              detail: `RGB: ${preset.rgb}`,
              apply: preset.rgb
            })
          }
        })
      }
    }
    
    // PlayAlertSound completions
    if (/PlayAlertSound\s+/.test(lineText)) {
      // Sound IDs
      if (!/PlayAlertSound\s+\d+/.test(lineText)) {
        gameData.soundIds.forEach(soundId => {
          if (soundId.toString().startsWith(currentWord)) {
            completions.push({
              label: soundId.toString(),
              type: 'number',
              detail: `Sound ${soundId}`
            })
          }
        })
      } else if (/PlayAlertSound\s+\d+\s+/.test(lineText)) {
        // Volume suggestions after sound ID
        [50, 100, 150, 200, 250, 300].forEach(volume => {
          if (volume.toString().startsWith(currentWord)) {
            completions.push({
              label: volume.toString(),
              type: 'number',
              detail: `Volume ${volume}`
            })
          }
        })
      }
    }
    
    // MinimapIcon completions - size, color, shape parameters
    if (/MinimapIcon\s+/.test(lineText)) {
      const parts = lineText.trim().split(/\s+/)
      const minimapIndex = parts.findIndex(part => part === 'MinimapIcon')
      
      if (minimapIndex !== -1) {
        const paramsAfterMinimap = parts.slice(minimapIndex + 1)
        const paramIndex = paramsAfterMinimap.length - 1

        if (paramIndex === 0 && currentWord.length > 0) {
          // First parameter: size (0-2)
          gameData.minimapIconSizes.forEach(size => {
            if (size.toString().startsWith(currentWord)) {
              completions.push({ 
                label: size.toString(), 
                type: 'number', 
                detail: 'Minimap icon size (0-2)' 
              })
            }
          })
        } else if (paramIndex === 1 && currentWord.length > 0) {
          // Second parameter: color
          gameData.effectColors.forEach(color => {
            if (color.toLowerCase().startsWith(currentWord.toLowerCase())) {
              completions.push({ 
                label: color, 
                type: 'constant', 
                detail: 'Minimap icon color' 
              })
            }
          })
        } else if (paramIndex === 2 && currentWord.length > 0) {
          // Third parameter: shape
          gameData.minimapIconShapes.forEach(shape => {
            if (shape.toLowerCase().startsWith(currentWord.toLowerCase())) {
              completions.push({ 
                label: shape, 
                type: 'constant', 
                detail: 'Minimap icon shape' 
              })
            }
          })
        }
      }
    }
    
    // 3. CONDITION VALUES (after operators) - with operators like =, !=, etc.
    const hasOperator = /[=<>!]=?/.test(lineText)

    completions.push(
      ...getConditionValueCompletions(lineText, currentWord, gameData.conditionRules)
    )
    
    if (hasOperator && currentWord.length > 0) {
      // BaseType values - real item names from game data
      if (lineText.includes('BaseType')) {
        gameData.baseTypes.forEach(baseType => {
          if (baseType.toLowerCase().includes(currentWord.toLowerCase())) {
            // If we're inside quotes, don't add quotes around the completion
            const label = isInsideQuotes ? baseType : `"${baseType}"`
            completions.push({ 
              label: label, 
              type: 'string', 
              detail: 'Base type' 
            })
          }
        })
      }
      
      // Class values - real item classes from game data  
      if (lineText.includes('Class')) {
        gameData.itemClasses.forEach(itemClass => {
          if (itemClass.toLowerCase().includes(currentWord.toLowerCase())) {
            // If we're inside quotes, don't add quotes around the completion
            const label = isInsideQuotes ? itemClass : `"${itemClass}"`
            completions.push({ 
              label: label, 
              type: 'string', 
              detail: 'Item class' 
            })
          }
        })
      }
      
    }
    
    // Return completions if found
    if (completions.length > 0) {
      return {
        from: line.from + wordStart,
        options: completions
      }
    }
    
    return null
  } catch (error) {
    return null
  }
}



// Export the autocomplete function
export function poeFilterAutocomplete(realm = 'pc') {
  // Load game data for the specific realm
  loadGameData(realm);
  return autocompletion({ override: [getCompletions] })
}
